/**
 * V4-ONBOARDING-TOUR 2026-06-18
 *
 * Hook qui contrôle l'affichage du tour produit (bulles explicatives react-joyride)
 * au premier login.
 *
 * Comportement :
 * - Persisté dans localStorage avec clé `raiso_tour_seen_<userId>` (per-user, pas per-space :
 *   le tour explique l'UI globale, vu une seule fois par compte).
 * - Default : non vu → le tour démarre (run=true) quand `ready` passe à true.
 * - `markSeen()` persiste le flag (appelé à la fin du tour ou au "Passer").
 * - `restart()` relance le tour à la demande (ex: bouton "Revoir le tutoriel" en Settings).
 *
 * Pourquoi localStorage et pas backend ? Préférence locale non critique ; un reset
 * (changement de device / clear storage) re-montre le tour, ce qui est acceptable.
 * La persistance backend multi-device est notée au backlog si le produit la demande.
 */
import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY_PREFIX = 'raiso_tour_seen_';

function getStorageKey(userId: string | undefined | null): string | null {
  if (!userId) return null;
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function readSeen(userId: string | undefined | null): boolean {
  const key = getStorageKey(userId);
  if (!key) return true; // pas d'user → ne pas lancer le tour
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return true; // localStorage indispo → ne pas spammer le tour
  }
}

function writeSeen(userId: string | undefined | null, seen: boolean): void {
  const key = getStorageKey(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, String(seen));
  } catch {
    // quota / disabled — dégradation silencieuse
  }
}

export interface UseProductTourParams {
  /** Id de l'utilisateur courant (clé de persistance). */
  userId: string | undefined | null;
  /**
   * True quand l'environnement est prêt à lancer le tour (DOM des cibles monté :
   * dashboard chargé, données initiales prêtes, pas de modale ouverte…).
   * Le caller décide de cette condition.
   */
  ready: boolean;
}

export interface UseProductTourResult {
  /** True si le tour doit tourner maintenant. */
  run: boolean;
  /** Marque le tour comme vu (fin / skip) et l'arrête. */
  markSeen: () => void;
  /** Relance le tour explicitement (réinitialise le flag). */
  restart: () => void;
}

export function useProductTour({ userId, ready }: UseProductTourParams): UseProductTourResult {
  const [seen, setSeen] = useState<boolean>(() => readSeen(userId));
  const [run, setRun] = useState(false);

  // Re-lire le flag si l'utilisateur change (logout/login, switch de compte).
  useEffect(() => {
    setSeen(readSeen(userId));
  }, [userId]);

  // Démarre le tour dès que prêt ET jamais vu. Ne le relance pas une fois vu.
  useEffect(() => {
    if (ready && !seen) {
      setRun(true);
    }
  }, [ready, seen]);

  const markSeen = useCallback(() => {
    writeSeen(userId, true);
    setSeen(true);
    setRun(false);
  }, [userId]);

  const restart = useCallback(() => {
    writeSeen(userId, false);
    setSeen(false);
    setRun(true);
  }, [userId]);

  return { run, markSeen, restart };
}
